import { useLocalObservable } from 'mobx-react-lite';
import { MutableRefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { popupsTasks } from '@gem/popups';
import { debounce, makeCancellablePromise, CancelPromiseHandler, patterns } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { MIN_SEARCH_LENGTH } from 'APP/constants/search';
import { User } from 'STORE/Users/User/User';

const globalEmailRegex = new RegExp(patterns.EMAIL_ADDRESS, 'g');

export interface IAutocomletePresenter {
  members: User[];
  isLoading: boolean;
  isReadyForSearch: boolean;
  searchText: string;
  isUserFromList: boolean;
  isOpenPopover: boolean;
  search: (searchValue: string) => void;
  cancelLoadMore: CancelPromiseHandler | null;
  canSelectMore: boolean;
  selectedUsersEmails: string[];
  saveSelectedUsersEmails(value: string[]): void;
  load(searchValue: string): Promise<void>;
  getIsSelected(candidate: User): boolean;
  onSearch(searchText: string): void;
  toggleContact(candidate: User): void;
  isNeedSearch(searchValue: string): boolean;
  forceAdd(email: string): void;
  resetSearch(): void;
  setOpenPopover(value: boolean): void;
  onClosePopover(): void;
  selectedUsers: User[];
  onFocusParticipants(): void;
}

interface IAutocomletePresenterProps {
  value: string[];
  onChange(emails: string[]): void;
  removeParticipant(email: string): void;
  setParticipant(useer: User): void;
}

const SEARCH_DELAY = 200;
const CHIPS_LIMIT = 50;

export function useAutocomletePresenter(props: IAutocomletePresenterProps): {
  presenter: IAutocomletePresenter;
  anchorRef: MutableRefObject<HTMLInputElement | null>;
} {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLInputElement | null>(null);

  const presenter = useLocalObservable<IAutocomletePresenter>(() => ({
    members: [],
    selectedUsersEmails: [],
    selectedUsers: [],
    searchText: '',
    isLoading: false,
    cancelLoadMore: null,
    isOpenPopover: false,

    get isReadyForSearch(): boolean {
      return presenter.searchText.length >= MIN_SEARCH_LENGTH;
    },

    get canSelectMore(): boolean {
      return presenter.selectedUsersEmails.length < CHIPS_LIMIT;
    },

    setOpenPopover(value: boolean): void {
      presenter.isOpenPopover = value;
    },

    onClosePopover(): void {
      presenter.setOpenPopover(false);
    },

    resetSearch(): void {
      presenter.members = [];
      presenter.isLoading = false;
    },

    saveSelectedUsersEmails(value: string[]): void {
      presenter.selectedUsersEmails = value;
    },

    toggleContact(user: User): void {
      const isExist = presenter.selectedUsersEmails.includes(user.email);

      if (isExist) {
        return;
      }

      if (!presenter.canSelectMore) {
        popupsTasks.showToast(t('calendar_alert_user_many_add'));
        return;
      }

      props.onChange([...presenter.selectedUsersEmails, user.email]);
      props.setParticipant(user);
    },

    get isUserFromList(): boolean {
      return !!presenter.members.find((member) => member.email === presenter.searchText);
    },

    forceAdd(email: string): void {
      const isExist = presenter.selectedUsersEmails.includes(email);

      if (isExist || !globalEmailRegex.test(presenter.searchText)) return;

      props.onChange([...presenter.selectedUsersEmails, email]);
      presenter.onSearch('');
    },

    getIsSelected(candidate: User): boolean {
      return presenter.selectedUsersEmails.includes(candidate.email);
    },

    isNeedSearch(searchValue: string): boolean {
      return presenter.searchText === searchValue;
    },

    onFocusParticipants(): void {
      if (presenter.isReadyForSearch && presenter.members.length) {
        presenter.setOpenPopover(true);
      }
    },

    onSearch(searchText: string): void {
      presenter.resetSearch();
      presenter.searchText = searchText;

      if (!presenter.isReadyForSearch) {
        return;
      }

      presenter.search(presenter.searchText);
    },

    search: debounce(async (searchValue: string) => {
      await presenter.load(searchValue);
    }, SEARCH_DELAY),

    async load(searchValue: string): Promise<void> {
      if (presenter.cancelLoadMore) {
        presenter.cancelLoadMore();
      }

      presenter.isLoading = true;

      const { promise: getUsers, cancel } = makeCancellablePromise(
        Tasks.calendar.searchUsersAddToCalendarEvent(presenter.searchText)
      );

      presenter.cancelLoadMore = cancel;

      const data = await getUsers;

      if (data && presenter.isNeedSearch(searchValue)) {
        const { users } = data;

        presenter.members.push(...users);
        // presenter.cursor = cursor;
      }

      presenter.cancelLoadMore = null;
      presenter.isLoading = false;
    },
  }));

  useEffect(() => {
    presenter.saveSelectedUsersEmails(props.value);
  }, [props.value]);

  useEffect(() => {
    presenter.setOpenPopover(!!presenter.members.length);
  }, [presenter.members.length]);

  return { presenter, anchorRef };
}
